const STORAGE_KEY = 'app-theme';
/*
 * LAZY, and not as a micro-optimisation.
 *
 * Created at module scope this reached for `window` the moment ANYTHING
 * imported this file — including a Node bundle that only wanted a type or a
 * sibling export. `uis/app-ui`'s tree test is bundled with esbuild and run
 * under node, and it died on this line before reaching a single assertion, so
 * a suite that existed did not run.
 *
 * A module-scope side effect makes a module importable only where its
 * environment already exists. Deferring to first use costs nothing in a browser
 * — same object, same lifetime, created a few microseconds later — and makes
 * the module honest about when it needs a DOM.
 */
let mediaQuery;
function mql() {
    return (mediaQuery ??= window.matchMedia('(prefers-color-scheme: dark)'));
}
const listeners = new Set();
// The choice made this page when storage is blocked: it cannot persist, but it must still apply,
// or the toggle looks broken.
let sessionPref;
export function getThemePref() {
    let v;
    try {
        v = localStorage.getItem(STORAGE_KEY);
    }
    catch {
        v = sessionPref ?? null; // private mode / storage disabled
    }
    // Default everyone to LIGHT; their explicit choice is saved to localStorage
    // and wins thereafter.
    return v === 'dark' || v === 'light' || v === 'auto' ? v : 'light';
}
export function getResolvedTheme() {
    const pref = getThemePref();
    if (pref === 'auto')
        return mql().matches ? 'dark' : 'light';
    return pref;
}
function apply(resolved) {
    const set = () => {
        document.documentElement.setAttribute('data-theme', resolved);
        for (const cb of listeners)
            cb(resolved);
    };
    // The whole UI flips in UNISON via ONE View Transition crossfade of the page
    // snapshot. This works cleanly because of the component rule below — no
    // component transitions background-color/color, so the live DOM snaps to the
    // new theme in a single frame and the View Transition crossfades the two
    // snapshots uniformly (no staggered per-element fade).
    //
    // RULE for every component: transition transform / opacity / border-color for
    // interactions; NEVER background-color or color. Theme handles those.
    if (typeof document.startViewTransition === 'function') {
        // BOTH rejections are swallowed, because aborting is normal: a transition is skipped when
        // another starts before it finishes or the tab is hidden mid-flight, which happens whenever
        // somebody clicks quickly. Nothing is lost by ignoring it — the update callback runs whether
        // the animation plays or is skipped, so the theme is already applied either way.
        //
        // .ready is the one that MATTERS and it was missing. A skipped transition RESOLVES .finished
        // and REJECTS .ready with InvalidStateError, so catching only .finished caught the promise
        // that never rejects and left the one that does unhandled. The comment here used to say the
        // rejection was handled while the uncaught InvalidStateError was still visible in the console
        // on rapid tab clicks — which is why this now catches both rather than the one that reads
        // like the natural choice.
        const vt = document.startViewTransition(set);
        vt.finished?.catch(() => { });
        vt.ready?.catch(() => { });
    }
    else
        set();
}
export function setThemePref(pref) {
    sessionPref = pref;
    try {
        localStorage.setItem(STORAGE_KEY, pref);
    }
    catch {
        /* private mode / storage disabled — applies for this page, just won't persist */
    }
    apply(getResolvedTheme());
}
/** Subscribe to resolved-theme changes; returns an unsubscribe fn. */
export function onThemeChange(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}
/** Call once at boot — applies the stored/auto theme and wires OS-change. */
export function initTheme() {
    apply(getResolvedTheme());
    mql().addEventListener('change', () => {
        if (getThemePref() === 'auto')
            apply(getResolvedTheme());
    });
}
//# sourceMappingURL=theme.js.map